import { inject } from '@angular/core';
import {
  CanActivateFn,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from './auth.service';

/**
 * Resuelve el destino de una sesión vigente: el ERP, o la pantalla de cambio de contraseña si
 * está vencida.
 *
 * @param authService servicio de sesión
 * @param router router para desviar al cambio de contraseña
 * @return `true` si se permite entrar al ERP; `false` si se desvió
 */
function resolverDestinoConSesion(authService: AuthService, router: Router): boolean {
  if (authService.isMustChangePasswordPending()) {
    router.navigate(['/cambiar-clave']);
    return false;
  }

  return true;
}

/**
 * Cierra la sesión y manda al login.
 *
 * @param authService servicio de sesión
 * @param router router de la aplicación
 * @return siempre `false`: la ruta pedida no se abre
 */
function expulsarAlLogin(authService: AuthService, router: Router): boolean {
  authService.clearAllData();
  router.navigate(['/login']);
  return false;
}

export const authGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
): boolean | Observable<boolean> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isSessionActive()) {
    return resolverDestinoConSesion(authService, router);
  }

  // El access token venció, o entró en el margen previo al vencimiento, pero el refresh token
  // vive mucho más: se intenta renovar antes de expulsar. Antes se limpiaba la sesión sin
  // intentarlo siquiera, así que bastaba con navegar en esa ventana para acabar en el login
  // teniendo en mano un refresh token perfectamente válido.
  if (!authService.getRefreshToken()) {
    return expulsarAlLogin(authService, router);
  }

  return authService.refreshToken().pipe(
    map(() => resolverDestinoConSesion(authService, router)),
    catchError(() => of(expulsarAlLogin(authService, router))),
  );
};

/**
 * Guard de la pantalla de cambio de contraseña vencida: solo accesible si hay sesión válida y un
 * cambio pendiente. Evita que se abra la ruta directamente sin venir del login.
 */
export const mustChangePasswordGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
): boolean => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isSessionActive() && authService.isMustChangePasswordPending()) {
    return true;
  }

  return expulsarAlLogin(authService, router);
};
